import { z } from 'zod';
import { PersonRef } from './people.js';
import { firstOccurrence, parseRepeatPrefix } from './myday-repeat.js';
import { addDaysIso, zonedParts, zonedToUtc } from './time.js';
import { WorkItemType } from './work-items.js';
/**
 * Notifications and reminders (docs/adr/0011-notifications-and-reminders.md).
 *
 * A notification is a pointer: ids plus a kind. Text is rendered when the inbox is read, from rows
 * the reader can still see, so a deleted message or a channel someone left disappears from the inbox
 * too, and no message body is copied into another table.
 */
export const NotificationKind = z.enum([
    'mention',
    'reply',
    'assigned',
    'status_changed',
    'reminder',
    'daily_summary',
    'sla_warning',
    'ticket_email',
    /** Someone you asked about is free (their status cleared). */
    'status_free',
    'support_hours',
    /** Owners and admins: someone reported a message (docs/adr/0039). Carries no channel or message id. */
    'report_received',
    /** The reporter: their report was reviewed, and whether action was taken. Never who acted. */
    'report_closed',
    /** Someone commented on a task you are assigned, created or commented on (docs/adr/0048). Folded per task. */
    'task_comment',
    /** A task assigned to you became overdue, in your time zone. Once per due date. */
    'task_overdue',
    /** A heads-up the day before a task assigned to you is due. */
    'task_due_soon',
    /** Someone started a catch-up in a channel you are in (docs/adr/0057). Once per catch-up. */
    'catch_up_started',
    /** Commit nudges (docs/adr/0059): an In progress task of yours has no linked commit for the project's threshold. Private. */
    'commit_nudge',
    /** The project's chosen lead: a second threshold passed on someone's task with still no commit. */
    'commit_nudge_lead',
    /** Your working day ended with In progress tasks and nothing pushed to them (personal opt-in). */
    'unpushed_work',
    /**
     * Owners: a verified single sign-on domain's DNS record is missing (`stage: warning`, with the date it stops
     * working) or the domain was marked unverified (`stage: unverified`). Carries the domain id only (docs/adr/0055).
     */
    'sso_domain',
    /**
     * A security notice (docs/adr/0023): an owner exported a copy of the workspace that includes this person's direct
     * messages or private channels. Not a type people choose: always in app and pushed, and always emailed.
     */
    'private_export',
    /**
     * Chapi's nudge about overdue invoices (docs/adr/0064): "3 invoices overdue", to owners and people granted "Manage
     * invoices", once per day or week when an owner turned it on (off by default). The count only, never an amount. Not
     * a type people choose (the owner's setting is the switch): in app and pushed, never emailed.
     */
    'invoices_overdue',
    /** Showcase (docs/adr/0068): new comments on a set of a project you are on (folded per set), for the team. */
    'design_comment',
    /** Someone @mentioned you in a design comment or reply. */
    'design_mention',
    /** A reply to your design comment, or the team marked it Done. Never an internal note to a client. */
    'design_reply',
    /** A design set was shared with you (clients). Its own email is the invitation, so this row is never emailed. */
    'design_shared',
    /** Your feedback on a design set is due in 2 days and you have not opened it yet (the set's optional reminder). */
    'design_review_due',
    /** A new version of a design set shared with you is ready (clients), with what changed (docs/adr/0069). */
    'design_version',
    /** A design set of a project you are on was approved (the team), with the approval record (docs/adr/0069). */
    'design_approved',
    /**
     * My day (docs/adr/0107): one of your own to-dos reached its time (or the minutes before it you chose). Carries the item id
     * and the occurrence's date and time; the title is read from the item when shown, for its owner only.
     */
    'todo_nudge',
    /** A project rule's notify step (docs/adr/0109): the rule and the task, rendered when read. */
    'rule',
]);
/** The phone registers its Expo push token for the workspace it is signed in to. */
export const PushTokenInput = z.object({ token: z.string().min(10).max(200), platform: z.enum(['ios', 'android']) });
export const RemovePushTokenInput = z.object({ token: z.string().min(10).max(200) });
export const WorkItemRef = z.object({
    id: z.uuid(),
    key: z.string(),
    title: z.string(),
    /** Task, job, support ticket or bug, when the server knows it (the Notifications centre sorts tickets under Support). */
    type: WorkItemType.optional(),
});
/** The start-of-day summary: one notification per person per working day, never one per item. */
export const DailySummary = z.object({
    date: z.string(),
    overdue: z.array(WorkItemRef),
    dueToday: z.array(WorkItemRef),
    stale: z.array(WorkItemRef),
    /** Owners and admins only: open items with no assignee or no due date for a while. */
    unowned: z.number().int(),
    unreadMentions: z.number().int(),
    /**
     * The daily digest (SC-20): unread counts from the last day for each type the person put in their digest (besides
     * mentions, counted above). Counts only, never what was said.
     */
    digest: z.array(z.object({ type: z.string().max(40), label: z.string().max(80), count: z.number().int() })).optional(),
});
// --- The Notifications centre (SC-20, docs/adr/0048) -------------------------------------------------
/** The centre's tabs. Which ones a person sees comes from the server (`NotificationList.tabUnread`). */
export const NotificationTab = z.enum(['all', 'mentions', 'assigned', 'designs', 'tasks', 'support']);
/** The part of the app a notification is about: the label under each row, and the tab it sorts into. */
export const NotificationArea = z.enum(['chat', 'tasks', 'support', 'designs', 'workspace']);
/** Kinds that are always about support (tickets, SLAs, support plans). */
export const SUPPORT_NOTIFICATION_KINDS = ['sla_warning', 'ticket_email', 'support_hours'];
/** Kinds that are about tasks even when they point at no single task (the daily summary, end-of-day nudge). */
export const TASK_NOTIFICATION_KINDS = ['assigned', 'status_changed', 'task_comment', 'task_overdue', 'task_due_soon', 'commit_nudge', 'commit_nudge_lead', 'unpushed_work', 'daily_summary', 'rule'];
/** Kinds that @mention the person (chat and design comments). */
export const MENTION_NOTIFICATION_KINDS = ['mention', 'design_mention'];
const CHAT_KINDS = ['mention', 'reply', 'thread_reply', 'catch_up_started', 'direct_message', 'status_free'];
/**
 * The area of a notification, by entity rather than by a list of kinds, so a new kind slots in: anything about a design
 * set (`design_*`, e.g. a new version or an approval) is Designs; a support kind or a ticket is Support; a task kind or
 * any other task is Tasks; a message is Chat. The API's tab filter is the same rule written in SQL.
 */
export function notificationArea(kind, entity) {
    if (kind.startsWith('design_'))
        return 'designs';
    if (SUPPORT_NOTIFICATION_KINDS.includes(kind) || entity.workItemType === 'support')
        return 'support';
    if (TASK_NOTIFICATION_KINDS.includes(kind) || entity.hasWorkItem || entity.workItemType)
        return 'tasks';
    if (CHAT_KINDS.includes(kind) || entity.hasChannel)
        return 'chat';
    return 'workspace';
}
/** Every tab a notification shows under (always All). */
export function notificationTabs(kind, area) {
    const out = ['all'];
    if (MENTION_NOTIFICATION_KINDS.includes(kind))
        out.push('mentions');
    if (kind === 'assigned')
        out.push('assigned');
    if (area === 'designs' || area === 'tasks' || area === 'support')
        out.push(area);
    return out;
}
export const NOTIFICATION_TAB_LABELS = {
    all: 'All',
    mentions: 'Mentions',
    assigned: 'Assigned to me',
    designs: 'Designs',
    tasks: 'Tasks',
    support: 'Support',
};
export const NOTIFICATION_AREA_LABELS = { chat: 'Chat', tasks: 'Tasks', support: 'Support', designs: 'Showcase', workspace: 'Chapsi' };
/**
 * The tabs a person sees: All and Mentions for everyone; Assigned to me and Tasks for the team while Tasks is on; Designs
 * while Showcase is on for them (a Showcase guest only has Showcase); Support for the team while Support is on. Guests
 * (Showcase guests and channel guests) never get the team's tabs; a channel guest keeps Tasks for comments on tasks
 * they can see. The API answers 404 for a tab that is not in this list.
 */
export function visibleNotificationTabs(opts) {
    const out = ['all', 'mentions'];
    if (!opts.guest && opts.feature('tasks'))
        out.push('assigned');
    if (opts.feature('showcase'))
        out.push('designs');
    if (!opts.showcaseGuest && opts.feature('tasks'))
        out.push('tasks');
    if (!opts.guest && opts.feature('support'))
        out.push('support');
    return out;
}
export const Notification = z.object({
    id: z.uuid(),
    kind: NotificationKind,
    /** One line, already rendered for the reader, e.g. "Mia mentioned you in #harbour-homes". */
    title: z.string(),
    /** A short excerpt the reader is allowed to see (message text, task title, reminder note). */
    excerpt: z.string().nullable(),
    actor: PersonRef.nullable(),
    channelId: z.uuid().nullable(),
    messageId: z.uuid().nullable(),
    workItem: WorkItemRef.nullable(),
    reminderId: z.uuid().nullable(),
    summary: DailySummary.nullable(),
    /** Showcase notifications (docs/adr/0068): the set, and the comment and screen when there is one. */
    design: z.object({ setId: z.uuid(), commentId: z.uuid().nullable(), screenId: z.uuid().nullable() }).nullable().optional(),
    /** A My day nudge (docs/adr/0107): the person's own item, opened on My day with the item highlighted. */
    todo: z.object({ itemId: z.uuid(), dueOn: z.string(), dueTime: z.string() }).nullable().optional(),
    /** Which part of the app it is about (SC-20): the row's label, and the tabs it sorts into (`notificationTabs`). */
    area: NotificationArea.optional(),
    readAt: z.string().nullable(),
    createdAt: z.string(),
});
/**
 * The optional entity ids a push carries for a notification (SC-20): the design set and comment for anything about a
 * design set, the ticket for a support ticket. Ids only, never a name or text, like the rest of the push.
 */
export function pushEntityIds(n) {
    const out = {};
    if (n.todo)
        out.todoItemId = n.todo.itemId;
    if (n.design) {
        out.designSetId = n.design.setId;
        if (n.design.commentId)
            out.designCommentId = n.design.commentId;
    }
    if (n.workItem?.type === 'support')
        out.ticketId = n.workItem.id;
    return out;
}
export const NotificationQuery = z.object({
    filter: z.enum(['all', 'unread']).default('all'),
    /** The Notifications centre's tab (SC-20). A tab the person does not have answers 404. */
    tab: NotificationTab.default('all'),
    /** Cursor: `nextCursor` from the previous page (opaque). */
    before: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(30),
});
export const NotificationList = z.object({
    items: z.array(Notification),
    /** Every unread notification the person sees (the sidebar and app-icon badge), whatever the tab. */
    unread: z.number().int(),
    /** Unread per tab, for the tabs this person has (only those keys are present). */
    tabUnread: z.partialRecord(NotificationTab, z.number().int()).optional(),
    nextCursor: z.string().nullable(),
});
export const MarkNotificationsInput = z
    .object({
    ids: z.array(z.uuid()).min(1).max(200).optional(),
    all: z.literal(true).optional(),
    /** With `all`: only that tab's notifications (Mark all read on the Mentions tab leaves the rest unread). */
    tab: NotificationTab.optional(),
})
    .refine((v) => !!v.ids !== !!v.all, { message: 'Pass either ids or all' })
    .refine((v) => !v.tab || !!v.all, { message: 'A tab goes with all' });
export const MarkNotificationsResponse = z.object({ unread: z.number().int(), tabUnread: z.partialRecord(NotificationTab, z.number().int()).optional() });
// --- Reminders ---------------------------------------------------------------------------------
export const ReminderTarget = z.object({
    messageId: z.uuid().nullable(),
    channelId: z.uuid().nullable(),
    channelName: z.string().nullable(),
    excerpt: z.string().nullable(),
    workItem: WorkItemRef.nullable(),
});
export const Reminder = z.object({
    id: z.uuid(),
    at: z.string(),
    note: z.string(),
    forUser: PersonRef,
    createdBy: PersonRef,
    target: ReminderTarget,
    deliveredAt: z.string().nullable(),
    createdAt: z.string(),
});
/** `for_others`: upcoming reminders you set for teammates. */
export const ReminderQuery = z.object({ state: z.enum(['upcoming', 'delivered', 'for_others']).default('upcoming') });
export const ReminderList = z.object({ items: z.array(Reminder) });
export const MAX_REMINDER_DAYS = 366;
export const CreateReminderInput = z
    .object({
    at: z.iso.datetime({ offset: true }),
    note: z.string().trim().max(500).default(''),
    messageId: z.uuid().optional(),
    workItemId: z.uuid().optional(),
    /** Remind someone else in the workspace. Guests can only remind themselves. */
    userId: z.uuid().optional(),
})
    .refine((v) => !(v.messageId && v.workItemId), { message: 'A reminder points at a message or a task, not both' });
export const UpdateReminderInput = z.object({ at: z.iso.datetime({ offset: true }) });
const UNITS = {
    m: 1, min: 1, mins: 1, minute: 1, minutes: 1,
    h: 60, hr: 60, hrs: 60, hour: 60, hours: 60,
    d: 1440, day: 1440, days: 1440,
    w: 10080, week: 10080, weeks: 10080,
};
const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const DEFAULT_HOUR = 9;
export function weekdayIndex(word) {
    const w = word.toLowerCase();
    if (w.length < 3)
        return -1;
    return DAY_NAMES.findIndex((d) => d.startsWith(w));
}
function monthIndex(word) {
    return MONTHS.indexOf(word.toLowerCase().slice(0, 3));
}
/** "9", "9am", "9:30", "9.30pm", "17:00", "noon", "midday". Bare numbers only when `bare` is set. */
export function parseTime(word, bare) {
    if (!word)
        return null;
    const w = word.toLowerCase();
    if (w === 'noon' || w === 'midday')
        return { h: 12, m: 0 };
    const match = /^(\d{1,2})(?:[:.](\d{2}))?(am|pm)?$/.exec(w);
    if (!match)
        return null;
    const [, hs, ms, ap] = match;
    if (!ap && !ms && !bare)
        return null;
    let h = Number(hs);
    const m = ms ? Number(ms) : 0;
    if (m > 59)
        return null;
    if (ap) {
        if (h < 1 || h > 12)
            return null;
        if (ap === 'pm' && h !== 12)
            h += 12;
        if (ap === 'am' && h === 12)
            h = 0;
    }
    else if (h > 23)
        return null;
    return { h, m };
}
/**
 * Reads a when-phrase from the start of `words`: `tomorrow 9am`, `Friday`, `in 2 hours`, `at 3pm`,
 * `20 Oct`, `next week`, `2026-10-20`. Shared by `/remind` and My day's quick add (docs/adr/0029-my-day.md).
 * Returns null when the words do not start with one, or an error for a day that does not exist.
 *
 * With `{ repeat: true }` (My day's quick add and `/todo`, docs/adr/0107) it also reads a repeat ("every monday 9am",
 * "every 2 weeks", "monthly"); without it, as for `/remind`, nothing it reads changes.
 */
export function parseWhenPrefix(input, now, timeZone, opts = {}) {
    const words = [...input];
    const today = zonedParts(now, timeZone);
    const todayIso = `${today.year}-${String(today.month).padStart(2, '0')}-${String(today.day).padStart(2, '0')}`;
    let date = null;
    let time = null;
    let offsetMinutes = null;
    const peek = (i = 0) => words[i]?.toLowerCase();
    const takeTime = () => {
        if (peek() === 'at') {
            const t = parseTime(words[1], true);
            if (t) {
                words.splice(0, 2);
                return t;
            }
        }
        const t = parseTime(words[0], false);
        if (t)
            words.shift();
        return t;
    };
    if (opts.repeat) {
        const r = parseRepeatPrefix(words, todayIso);
        if (r) {
            words.splice(0, r.consumed);
            const time = takeTime();
            // A bare "daily" or "monthly" is a repeat only at the end ("daily stand-up notes" is a title).
            if (!r.bare || !words.length)
                return { date: firstOccurrence(r.rule, todayIso), time, offsetMinutes: null, consumed: input.length - words.length, repeat: r.rule };
            words.splice(0, words.length, ...input);
        }
    }
    if (peek() === 'in' && words[1] && /^\d+(\.\d+)?$/.test(words[1]) && UNITS[peek(2) ?? '']) {
        offsetMinutes = Math.round(Number(words[1]) * UNITS[peek(2)]);
        words.splice(0, 3);
    }
    else if (peek() === 'in' && words[1] && /^\d+[a-z]+$/i.test(words[1])) {
        const m = /^(\d+)([a-z]+)$/i.exec(words[1]);
        const unit = UNITS[m[2].toLowerCase()];
        if (unit) {
            offsetMinutes = Number(m[1]) * unit;
            words.splice(0, 2);
        }
    }
    if (offsetMinutes === null) {
        if (peek() === 'on')
            words.shift();
        const w0 = peek();
        if (w0 === 'today') {
            words.shift();
            date = todayIso;
        }
        else if (w0 === 'tomorrow' || w0 === 'tmrw') {
            words.shift();
            date = addDaysIso(todayIso, 1);
        }
        else if (w0 === 'next' && peek(1) === 'week') {
            words.splice(0, 2);
            date = addDaysIso(todayIso, ((1 - today.weekday + 7) % 7) || 7);
        }
        else if ((w0 === 'next' && weekdayIndex(words[1] ?? '') >= 0) || weekdayIndex(words[0] ?? '') >= 0) {
            if (w0 === 'next')
                words.shift();
            const dow = weekdayIndex(words.shift());
            date = addDaysIso(todayIso, ((dow - today.weekday + 7) % 7) || 7);
        }
        else if (w0 && /^\d{4}-\d{2}-\d{2}$/.test(w0)) {
            words.shift();
            date = w0;
        }
        else if (w0 && /^\d{1,2}(st|nd|rd|th)?$/.test(w0) && monthIndex(words[1] ?? '') >= 0) {
            date = isoFromDayMonth(Number.parseInt(w0, 10), monthIndex(words[1]), today, todayIso);
            words.splice(0, 2);
            if (!date)
                return { error: 'That date does not exist' };
        }
        else if (w0 && monthIndex(w0) >= 0 && /^\d{1,2}(st|nd|rd|th)?$/.test(peek(1) ?? '')) {
            date = isoFromDayMonth(Number.parseInt(words[1], 10), monthIndex(w0), today, todayIso);
            words.splice(0, 2);
            if (!date)
                return { error: 'That date does not exist' };
        }
        time = takeTime();
        if (!date && !time)
            return null;
    }
    return { date, time, offsetMinutes, consumed: input.length - words.length };
}
/**
 * Parses what follows `/remind`: `me tomorrow 9am to send the invoice`, `<@user-id> Friday check the
 * staging build`, `me in 2 hours`, `me at 3pm about the call`, `me 20 Oct follow up`. Days without a
 * time mean 9 am. Times are read in the person's time zone.
 */
export function parseRemindCommand(input, now, timeZone) {
    const words = input.trim().replace(/^\/remind\s*/i, '').split(/\s+/).filter(Boolean);
    if (!words.length)
        return { ok: false, error: 'Try /remind me tomorrow 9am to send the invoice' };
    let who;
    const first = words.shift();
    const mention = /^<@([0-9a-f-]{36})>$/i.exec(first);
    if (first.toLowerCase() === 'me')
        who = 'me';
    else if (mention)
        who = { userId: mention[1].toLowerCase() };
    else
        return { ok: false, error: 'Start with "me" or @someone, e.g. /remind me tomorrow 9am' };
    const today = zonedParts(now, timeZone);
    const todayIso = `${today.year}-${String(today.month).padStart(2, '0')}-${String(today.day).padStart(2, '0')}`;
    const when = parseWhenPrefix(words, now, timeZone);
    if (when && 'error' in when)
        return { ok: false, error: when.error };
    if (!when)
        return { ok: false, error: 'Say when, e.g. tomorrow 9am, Friday, in 2 hours or at 3pm' };
    words.splice(0, when.consumed);
    const { offsetMinutes, time } = when;
    let date = when.date;
    if (['to', 'about', 'that'].includes(words[0]?.toLowerCase() ?? ''))
        words.shift();
    const note = words.join(' ').slice(0, 500);
    let at;
    if (offsetMinutes !== null) {
        if (offsetMinutes < 1)
            return { ok: false, error: 'Pick a time in the future' };
        at = new Date(now.getTime() + offsetMinutes * 60_000);
    }
    else {
        if (date === null) {
            // "at 3pm" alone: today, or tomorrow if that time has passed.
            const t = time;
            date = t.h * 60 + t.m > today.hour * 60 + today.minute ? todayIso : addDaysIso(todayIso, 1);
        }
        const [y, mo, d] = date.split('-').map(Number);
        const t = time ?? { h: DEFAULT_HOUR, m: 0 };
        at = zonedToUtc(y, mo, d, t.h, t.m, timeZone);
        if (Number.isNaN(at.getTime()))
            return { ok: false, error: 'That date does not exist' };
        if (at.getTime() <= now.getTime())
            return { ok: false, error: 'That time has already passed' };
    }
    if (at.getTime() - now.getTime() > MAX_REMINDER_DAYS * 86_400_000)
        return { ok: false, error: 'Reminders can be at most a year away' };
    return { ok: true, who, at, note };
}
function isoFromDayMonth(day, month, today, todayIso) {
    if (day < 1 || day > 31)
        return null;
    const pad = (n) => String(n).padStart(2, '0');
    let iso = `${today.year}-${pad(month + 1)}-${pad(day)}`;
    const check = new Date(`${iso}T00:00:00Z`);
    if (check.getUTCDate() !== day)
        return null;
    if (iso < todayIso)
        iso = `${today.year + 1}-${pad(month + 1)}-${pad(day)}`;
    return iso;
}
//# sourceMappingURL=notifications.js.map